import { AnimatePresence, motion } from 'framer-motion'
import { Check, CircleDashed, Loader2, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Progress } from '#app/components/ui/progress'
import { cn } from '#app/utils/misc'

type StepStatus = 'pending' | 'processing' | 'completed' | 'failed'

export interface ProgressStep {
	id: string
	name: string
	status: StepStatus
	label: string
}

interface ReportProgressOverlayProps {
	variant: 'individual' | 'family'
	steps: ProgressStep[]
	overall: 'processing' | 'completed' | 'failed'
	applicantName?: string
	/** Optional carousel copy override (pre-screening omits credit/court
	 * tips). Omitted → the variant defaults below, byte-identical for v1. */
	tips?: string[]
}

const INDIVIDUAL_TIPS = [
	'Pulling credit report data...',
	'Checking public court records...',
	'Analyzing financial profile...',
	'Running AI risk assessment...',
	'Calculating TrueTenant score...',
	'Compiling verification results...',
]

const FAMILY_TIPS = [
	'Collecting applicant profiles...',
	'Analyzing household income...',
	'Evaluating combined risk factors...',
	'Calculating family TrueTenant score...',
	'Generating combined summary...',
]

function StepIcon({ status }: { status: StepStatus }) {
	switch (status) {
		case 'completed':
			return (
				<motion.div
					initial={{ scale: 0 }}
					animate={{ scale: 1 }}
					className="flex h-7 w-7 items-center justify-center rounded-full bg-green-500"
				>
					<Check className="h-4 w-4 text-white" />
				</motion.div>
			)
		case 'processing':
			return (
				<div className="flex h-7 w-7 items-center justify-center rounded-full bg-[#32302F]">
					<Loader2 className="h-4 w-4 animate-spin text-white" />
				</div>
			)
		case 'failed':
			return (
				<motion.div
					initial={{ scale: 0 }}
					animate={{ scale: 1 }}
					className="flex h-7 w-7 items-center justify-center rounded-full bg-red-500"
				>
					<X className="h-4 w-4 text-white" />
				</motion.div>
			)
		default:
			return (
				<div className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-gray-200 bg-white">
					<CircleDashed className="h-4 w-4 text-gray-300" />
				</div>
			)
	}
}

function computeProgress(steps: ProgressStep[]): number {
	if (steps.length === 0) return 0
	const completedCount = steps.filter((s) => s.status === 'completed').length
	const processingCount = steps.filter((s) => s.status === 'processing').length
	return Math.round(
		((completedCount + processingCount * 0.5) / steps.length) * 100,
	)
}

export function ReportProgressOverlay({
	variant,
	steps,
	overall,
	applicantName,
	tips: tipsProp,
}: ReportProgressOverlayProps) {
	const [tipIndex, setTipIndex] = useState(0)
	const tips =
		tipsProp ?? (variant === 'individual' ? INDIVIDUAL_TIPS : FAMILY_TIPS)

	useEffect(() => {
		if (overall !== 'processing') return
		const interval = setInterval(() => {
			setTipIndex((prev) => (prev + 1) % tips.length)
		}, 3500)
		return () => clearInterval(interval)
	}, [overall, tips.length])

	const progress = computeProgress(steps)
	const title =
		variant === 'family'
			? 'Generating Family Report'
			: `Generating Report${applicantName ? ` for ${applicantName}` : ''}`

	return (
		<div className="flex items-center justify-center py-12">
			<motion.div
				initial={{ opacity: 0, y: 12 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.35 }}
				className="w-full max-w-md rounded-2xl border border-[#E5E5E5] bg-white p-8 shadow-sm"
			>
				{/* Pulsing header icon */}
				<div className="mb-5 flex flex-col items-center">
					<motion.div
						animate={{ scale: [1, 1.08, 1] }}
						transition={{
							repeat: Number.POSITIVE_INFINITY,
							duration: 2,
							ease: 'easeInOut',
						}}
						className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#F3F4F6]"
					>
						<Loader2 className="h-7 w-7 animate-spin text-[#32302F]" />
					</motion.div>
					<h3 className="text-lg font-semibold text-[#32302F]">{title}</h3>
				</div>

				{/* Progress bar */}
				<Progress
					value={progress}
					className="mb-6 h-1.5 bg-gray-100 [&>div]:bg-[#32302F] [&>div]:transition-all [&>div]:duration-700"
				/>

				{/* Steps stepper */}
				<div className="mb-6 space-y-0">
					{steps.map((step, i) => (
						<div key={step.id} className="flex items-start gap-3">
							{/* Vertical connector + icon column */}
							<div className="flex flex-col items-center">
								<StepIcon status={step.status} />
								{i < steps.length - 1 && (
									<div
										className={cn(
											'my-0.5 w-0.5 flex-1 min-h-[20px]',
											step.status === 'completed'
												? 'bg-green-300'
												: 'bg-gray-200',
										)}
									/>
								)}
							</div>

							{/* Step text */}
							<div className="pb-4 pt-0.5">
								<p
									className={cn(
										'text-sm font-medium',
										step.status === 'completed'
											? 'text-green-700'
											: step.status === 'processing'
												? 'text-[#32302F]'
												: step.status === 'failed'
													? 'text-red-600'
													: 'text-gray-400',
									)}
								>
									{step.name}
								</p>
								<p
									className={cn(
										'text-xs',
										step.status === 'completed'
											? 'text-green-500'
											: step.status === 'failed'
												? 'text-red-400'
												: 'text-gray-400',
									)}
								>
									{step.label}
								</p>
							</div>
						</div>
					))}
				</div>

				{/* Cycling tip message */}
				{overall === 'processing' && (
					<div className="h-5 overflow-hidden text-center">
						<AnimatePresence mode="wait">
							<motion.p
								key={tipIndex}
								initial={{ opacity: 0, y: 8 }}
								animate={{ opacity: 1, y: 0 }}
								exit={{ opacity: 0, y: -8 }}
								transition={{ duration: 0.25 }}
								className="text-xs text-[#9CA3AF]"
							>
								{tips[tipIndex]}
							</motion.p>
						</AnimatePresence>
					</div>
				)}

				{overall === 'failed' && (
					<p className="text-center text-xs text-red-500">
						Something went wrong. Please try again.
					</p>
				)}
			</motion.div>
		</div>
	)
}
